'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent } from 'react';
import type { SectionMenuBlockProps } from '../types';
import { theme } from '../theme';
import { sectionAnchorId, UNLISTED_SECTION_TYPES } from '../sectionAnchors';

/**
 * Section menu: a bar of jump links to sections on the same page, like a listing page's
 * "Property Details · Schools · Payment Calculator" bar.
 *
 * - The sections come from the page itself: every section is rendered with its id
 *   (sectionAnchors.ts), so this reads them, and the heading each one shows, off the page it is on. `sections` picks which and in
 *   what order (with an optional label each); without it, every section after the menu that has
 *   a heading is listed under that heading.
 * - showWhen 'scroll' (default): nothing shows in place; once the visitor scrolls past where the
 *   menu sits, the bar appears fixed under the site header. 'always': the bar sits in place and
 *   sticks under the header as the page scrolls.
 * - A click scrolls smoothly to the section, clear of the header and the bar. The section in view
 *   is underlined; the underline only moves once a section is clearly past the line (a few px
 *   either way), so a slow scroll across a boundary does not flicker. On a phone the links are one
 *   row the visitor swipes sideways.
 * - Links go to each section's element id as rendered (unique on the page, sectionAnchorIds).
 *
 * Works in the visitor's window and inside the Studio's preview frame alike (it uses the window
 * of the document it is rendered in).
 */

interface Item {
  /** The section's block id. */
  id: string;
  /** Its element id on the page: what the link jumps to. */
  anchor: string;
  label: string;
}

/** The accessible name of the bar, so a screen reader user can find it among the landmarks. */
export const SECTION_MENU_LABEL = 'Page sections';

/** How far (px) a section must be past the line before the underline moves to or from it. */
const SWITCH_MARGIN = 12;

/**
 * The section in view: the last one whose top has reached `line`. Near a boundary it keeps
 * `current` while that is still in view within SWITCH_MARGIN either way, so the underline does not
 * flicker between two sections as the visitor scrolls slowly across the line between them.
 */
export function sectionInView(tops: ReadonlyArray<number | null>, line: number, current: number): number {
  const lastAt = (y: number) => {
    let at = -1;
    tops.forEach((t, i) => { if (t !== null && t <= y) at = i; });
    return at;
  };
  const early = lastAt(line - SWITCH_MARGIN);
  const late = lastAt(line + SWITCH_MARGIN);
  if (current >= early && current <= late && current >= 0) return current;
  return lastAt(line);
}

/** The element a section's wrapper lays out as: itself, or its first child for a `display: contents` wrapper. */
function sectionBox(wrapper: HTMLElement): HTMLElement | null {
  const win = wrapper.ownerDocument.defaultView;
  const contents = win ? win.getComputedStyle(wrapper).display === 'contents' : false;
  return contents ? (wrapper.firstElementChild as HTMLElement | null) : wrapper;
}

/** How far down the fixed/sticky site header reaches, in px (0 when it does not stay on screen). */
function headerBottom(doc: Document): number {
  const header = doc.querySelector<HTMLElement>('[data-site-header]') ?? doc.querySelector<HTMLElement>('header');
  if (!header) return 0;
  const pos = doc.defaultView?.getComputedStyle(header).position;
  if (pos !== 'fixed' && pos !== 'sticky') return 0;
  return Math.max(0, header.getBoundingClientRect().bottom);
}

/** The heading a section shows on the page ('' when it shows none, or shows nothing at all). */
function shownHeading(wrapper: HTMLElement): string {
  const h = wrapper.querySelector('h1, h2, h3');
  return (h?.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 60);
}

/**
 * The sections rendered together with the menu: the section wrappers beside its own (one page's
 * BlockRenderer). Scoping to them keeps a second set of sections in the same document, say a
 * gallery of template previews, out of this menu's list and its jumps.
 */
function pageSections(own: HTMLElement): HTMLElement[] {
  const parent = own.closest<HTMLElement>('[data-block-id]')?.parentElement;
  if (!parent) return Array.from(own.ownerDocument.querySelectorAll<HTMLElement>('[data-block-id]'));
  return Array.from(parent.children).filter((c): c is HTMLElement => (c as HTMLElement).dataset?.blockId !== undefined);
}

/** The section on this menu's page with that element id (never one from another set of sections). */
function sectionByAnchor(own: HTMLElement, anchor: string): HTMLElement | null {
  return pageSections(own).find((w) => w.id === anchor) ?? null;
}

/** The page's sections this menu lists, read off the rendered page. */
export function readSectionItems(own: HTMLElement | null, sections: SectionMenuBlockProps['sections']): Item[] {
  if (!own) return [];
  const wrappers = pageSections(own);
  const label = shownHeading;
  const anchorOf = (w: HTMLElement) => w.id || sectionAnchorId(w.dataset.blockId) || `section-${wrappers.indexOf(w) + 1}`;
  const chosen = Array.isArray(sections) ? sections.filter((s) => s && typeof s.id === 'string' && s.id) : [];
  if (chosen.length > 0) {
    // The first section with that id (a repeated id's later copies have their own anchors).
    const byId = new Map<string, HTMLElement>();
    for (const w of wrappers) if (!byId.has(w.dataset.blockId ?? '')) byId.set(w.dataset.blockId ?? '', w);
    return chosen
      .map((s) => {
        const w = byId.get(s.id);
        // Gone, hidden, or showing nothing: not listed.
        if (!w || !w.firstElementChild) return null;
        const words = (s.label ?? '').trim() || label(w) || 'Section';
        return { id: s.id, anchor: anchorOf(w), label: words };
      })
      .filter((x): x is Item => !!x);
  }
  const mine = own.closest<HTMLElement>('[data-block-id]');
  const start = mine ? wrappers.indexOf(mine) + 1 : 0;
  return wrappers
    .slice(start)
    .filter((w) => !UNLISTED_SECTION_TYPES.has(w.dataset.blockType ?? '') && label(w))
    .map((w) => ({ id: w.dataset.blockId ?? '', anchor: anchorOf(w), label: label(w) }));
}

export function SectionMenuBlock({ sections, showWhen }: SectionMenuBlockProps) {
  const always = showWhen === 'always';
  const anchorRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLElement>(null);
  const linkRefs = useRef(new Map<string, HTMLAnchorElement>());
  const [items, setItems] = useState<Item[]>([]);
  const [shown, setShown] = useState(always);
  const [active, setActive] = useState<string | null>(null);
  const [top, setTop] = useState(0);

  // Read the page's sections after render, and again when the page's sections change (the
  // Studio preview re-renders sections in place as they are edited).
  const sectionsKey = JSON.stringify(sections ?? []);
  useEffect(() => {
    const own = anchorRef.current;
    if (!own) return;
    // Only a real change re-renders: the bar's own render is a DOM change the observer sees.
    const read = () => {
      const next = readSectionItems(own, sections);
      setItems((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    read();
    const win = own.ownerDocument.defaultView;
    if (!win || typeof win.MutationObserver !== 'function') return;
    const mo = new win.MutationObserver(read);
    mo.observe(own.ownerDocument.body, { childList: true, subtree: true, characterData: true });
    return () => mo.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionsKey]);

  // Show the bar, and follow the section in view, as the page scrolls (once per frame).
  const activeRef = useRef<string | null>(null);
  // While a click's smooth scroll is running, the clicked link stays underlined: the sections it
  // passes on the way are not "in view". Cleared once the page stops scrolling.
  const jumpingRef = useRef<string | null>(null);
  const settleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const own = anchorRef.current;
    const win = own?.ownerDocument.defaultView;
    if (!own || !win) return;
    const doc = own.ownerDocument;
    const update = () => {
      const headerAt = headerBottom(doc);
      setTop(headerAt);
      if (!always) setShown(own.getBoundingClientRect().top < headerAt);
      if (jumpingRef.current) return;
      const barH = barRef.current?.getBoundingClientRect().height ?? 0;
      const line = headerAt + barH + 8;
      const tops = items.map((it) => {
        const w = sectionByAnchor(own, it.anchor);
        const box = w ? sectionBox(w) : null;
        return box ? box.getBoundingClientRect().top : null;
      });
      const at = sectionInView(tops, line, items.findIndex((it) => it.anchor === activeRef.current));
      const next = at >= 0 ? items[at].anchor : null;
      activeRef.current = next;
      setActive(next);
    };
    let frame = 0;
    const onScroll = () => {
      if (jumpingRef.current) {
        if (settleRef.current) clearTimeout(settleRef.current);
        settleRef.current = setTimeout(() => {
          jumpingRef.current = null;
          update();
        }, 150);
      }
      if (frame) return;
      frame = win.requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };
    update();
    win.addEventListener('scroll', onScroll, { passive: true });
    win.addEventListener('resize', onScroll);
    // The header can change height without a scroll (a phone menu opening, a header that shrinks,
    // a font loading): follow it so the bar stays right under it.
    const header = doc.querySelector<HTMLElement>('[data-site-header]') ?? doc.querySelector<HTMLElement>('header');
    const ro = header && typeof win.ResizeObserver === 'function' ? new win.ResizeObserver(onScroll) : null;
    if (header) ro?.observe(header);
    return () => {
      if (frame) win.cancelAnimationFrame(frame);
      if (settleRef.current) clearTimeout(settleRef.current);
      ro?.disconnect();
      win.removeEventListener('scroll', onScroll);
      win.removeEventListener('resize', onScroll);
    };
  }, [items, always]);

  // Keep the underlined link in view on a phone, where the row scrolls sideways. Only the row
  // scrolls: scrollIntoView would also move the page, and stop a jump that is still scrolling.
  useEffect(() => {
    const link = active ? linkRefs.current.get(active) : null;
    const row = link?.closest('ul');
    if (!link || !row) return;
    const left = link.offsetLeft - row.offsetLeft;
    const right = left + link.offsetWidth;
    if (left >= row.scrollLeft && right <= row.scrollLeft + row.clientWidth) return;
    const to = left < row.scrollLeft ? left - 16 : right - row.clientWidth + 16;
    if (typeof row.scrollTo === 'function') row.scrollTo({ left: Math.max(0, to), behavior: 'smooth' });
    else row.scrollLeft = Math.max(0, to);
  }, [active]);

  const jump = useCallback((e: MouseEvent<HTMLAnchorElement>, anchor: string) => {
    const own = anchorRef.current;
    const win = own?.ownerDocument.defaultView;
    const w = own ? sectionByAnchor(own, anchor) : null;
    const box = w ? sectionBox(w) : null;
    if (!win || !box) return;
    e.preventDefault();
    const barH = barRef.current?.getBoundingClientRect().height ?? 0;
    const offset = headerBottom(own!.ownerDocument) + barH + 4;
    activeRef.current = anchor;
    jumpingRef.current = anchor;
    setActive(anchor);
    // Released when the scroll settles (above); this covers a jump that needs no scrolling.
    if (settleRef.current) clearTimeout(settleRef.current);
    settleRef.current = setTimeout(() => { jumpingRef.current = null; }, 1000);
    win.scrollTo({ top: box.getBoundingClientRect().top + win.scrollY - offset, behavior: 'smooth' });
  }, []);

  const bar = (
    <nav
      ref={barRef}
      aria-label={SECTION_MENU_LABEL}
      data-section-menu-bar=""
      style={{
        background: theme.background,
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
        ...(always
          ? {}
          : {
              position: 'fixed',
              top,
              left: 0,
              right: 0,
              zIndex: 40,
              transform: shown ? 'translateY(0)' : 'translateY(-110%)',
              opacity: shown ? 1 : 0,
              pointerEvents: shown ? 'auto' : 'none',
              transition: 'transform 200ms ease, opacity 200ms ease',
            }),
      }}
    >
      <ul
        className="mx-auto flex max-w-6xl gap-6 overflow-x-auto px-4 sm:px-6"
        style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {items.map((it) => {
          const on = it.anchor === active;
          return (
            <li key={it.anchor} className="shrink-0">
              <a
                ref={(el) => {
                  if (el) linkRefs.current.set(it.anchor, el);
                  else linkRefs.current.delete(it.anchor);
                }}
                href={`#${it.anchor}`}
                onClick={(e) => jump(e, it.anchor)}
                aria-current={on ? 'location' : undefined}
                className="block whitespace-nowrap py-3 text-sm font-medium transition-colors"
                style={{
                  color: on ? theme.text : theme.textMuted,
                  fontFamily: theme.bodyFont,
                  borderBottom: `3px solid ${on ? theme.primary : 'transparent'}`,
                }}
              >
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );

  return (
    // 'always' sticks the whole block (its wrapper on the live page lays out as nothing, so it
    // sticks within the page, not within a box its own height).
    <div
      ref={anchorRef}
      data-section-menu={always ? 'always' : 'scroll'}
      style={always ? ({ position: 'sticky', top, zIndex: 40 } as CSSProperties) : undefined}
    >
      {items.length > 0 ? bar : null}
    </div>
  );
}
